import { Validator, type OutputUnit, type Schema } from "@cfworker/json-schema";
import {
  CodemodeConnector,
  type ConnectorTool,
  type ConnectorTools,
  type ExecutionEndStatus,
  type PassEndStatus,
  type ToolExecuteContext
} from "@cloudflare/codemode";
import { CdpSession, connectUrl } from "./cdp-session";
import {
  connectBrowser,
  connectBrowserSession,
  createBrowserSession,
  deleteBrowserSession,
  listBrowserTargets,
  BrowserRenderingError,
  type BrowserBinding,
  type BrowserSessionInfo,
  type BrowserTargetInfo
} from "./browser-run";
import { loadCdpSpec, type SearchableCdpSpec } from "./spec";
import type {
  BrowserSessionStore,
  StoredBrowserSession
} from "./session-manager";
import { DEFAULT_SWEEP_IDLE_MS } from "./session-manager";

/** Browser session lifecycle for the connector (binding-backed only). */
export interface BrowserConnectorSessionOptions {
  /**
   * - `"one-shot"` (default) — one Browser Run session per codemode
   *   execution, deleted when the execution ends.
   * - `"reuse"` — all executions share one stored session under `key`.
   * - `"dynamic"` — per-execution sessions by default; the model can call
   *   `cdp.startSession()` to promote the current session into the shared
   *   slot so later executions reuse it.
   */
  mode?: "one-shot" | "reuse" | "dynamic";
  /** Logical owner key for the shared (reuse/promoted) session. Default `"default"`. */
  key?: string;
  /** Browser Run inactivity timeout. Browser Run currently caps this server-side. */
  keepAliveMs?: number;
  /**
   * Opt into Browser Run [session recording](https://developers.cloudflare.com/browser-run/features/session-recording/)
   * for sessions this connector creates: an rrweb capture of everything the
   * agent did, finalized when the session closes. Retrieve it afterward with
   * {@link getBrowserRecording} using the session id from
   * {@link BrowserConnector.liveView}/`sessionInfo()`.
   */
  recording?: boolean;
  /**
   * Select the browser engine. Defaults to Chromium. Kitesurf is scoped to
   * one CDP WebSocket, so it supports only one-shot execution without
   * pause/resume, Live View, recording, or keep-alive.
   */
  browser?: "kitesurf";
}

export type BrowserConnectorOptions = (
  | {
      /** Browser Rendering binding (Fetcher) — used in production. */
      browser: BrowserBinding;
      /**
       * Durable store for Browser Run session ids. Required with the binding:
       * a session must survive a pause (approval) and resume on a fresh
       * instance, so its id cannot live in connector memory.
       */
      store: BrowserSessionStore;
      session?: BrowserConnectorSessionOptions;
      cdpUrl?: never;
      cdpHeaders?: never;
    }
  | {
      /**
       * CDP base URL override (e.g. http://localhost:9222). The browser is
       * externally managed: no Browser Run sessions are created or deleted,
       * and session modes don't apply.
       */
      cdpUrl: string;
      /** Headers to send with CDP URL discovery requests (e.g. Access headers). */
      cdpHeaders?: Record<string, string>;
      browser?: never;
      store?: never;
      session?: never;
    }
) & {
  /** CDP command timeout in milliseconds (default: 10000). */
  timeout?: number;
};

export interface BrowserConnectorSweepOptions {
  /**
   * Close the shared (reuse/promoted) session when idle for at least this
   * many milliseconds. Defaults to the connector's `keepAliveMs`, or
   * {@link DEFAULT_SWEEP_IDLE_MS}.
   */
  maxIdleMs?: number;
  /**
   * Close *per-execution* sessions when idle for at least this many
   * milliseconds. Defaults to {@link DEFAULT_EXEC_SWEEP_IDLE_MS} (24h) —
   * deliberately at least as long as the codemode runtime's default paused
   * TTL, so a run awaiting approval is normally expired (and disposed) by
   * `expirePaused` before the sweep backstop ever touches its browser.
   */
  maxExecIdleMs?: number;
}

export interface BrowserConnectorSweepResult {
  /** Store keys (and their Browser Run session ids) closed by this sweep. */
  swept: Array<{ key: string; sessionId: string }>;
}

/**
 * Live View rendering mode (the `mode` query param the hosted UI at
 * `live.browser.run` understands):
 *
 * - `"tab"` — a standalone, interactive page view (best for handing control
 *   to a human).
 * - `"devtools"` — the full DevTools inspector panel (Elements, Console,
 *   Network, …).
 *
 * Omit it to use whatever mode the binding's `devtoolsFrontendUrl` defaults
 * to.
 */
export type LiveViewMode = "tab" | "devtools";

/** A single tab's Live View URL. */
export interface BrowserLiveViewUrl {
  /** Open this in a browser to watch/control the tab in real time. */
  url: string;
  /** CDP target (tab) id the URL points at. */
  targetId: string;
  /** Milliseconds the URL stays valid from when it was generated (~5 min). */
  expiresInMs: number;
}

export interface BrowserLiveViewTarget {
  targetId: string;
  /** Embeddable Live View URL (the `devtoolsFrontendUrl`) for this tab. */
  url: string;
  /** The page the tab is currently showing (e.g. `https://example.com`). */
  pageUrl?: string;
  title?: string;
  type?: string;
}

/** Live View URLs for every tab in a (shared) session. */
export interface BrowserLiveView {
  sessionId: string;
  targets: BrowserLiveViewTarget[];
  /** Milliseconds the URLs stay valid from when they were generated (~5 min). */
  expiresInMs: number;
}

const EXEC_KEY_PREFIX = "cdp:exec:";
/**
 * Prefix of the synthetic session id stored for a Kitesurf execution: the
 * connection is scoped to its execution, so the entry is a marker rather than
 * a Browser Run session that can be reconnected to or deleted.
 */
const KITESURF_SESSION_PREFIX = "kitesurf:";
const REUSE_KEY_PREFIX = "cdp:reuse:";

/**
 * Default idle window before {@link BrowserConnector.sweep} reclaims a
 * per-execution session. Matches the codemode runtime's default paused TTL
 * (24h): an execution paused for approval keeps its browser until the run
 * itself is expired.
 */
export const DEFAULT_EXEC_SWEEP_IDLE_MS = 24 * 60 * 60 * 1000;

/**
 * Minimum interval between `updatedAt` bumps on a per-execution store entry.
 * Touching on every CDP call would write-amplify; once a minute is enough to
 * keep an active or recently-resumed execution out of sweep range.
 */
const EXEC_TOUCH_INTERVAL_MS = 60 * 1000;

/**
 * Browser Run mints `devtoolsFrontendUrl`s (the Live View links) that are
 * valid for ~5 minutes. We surface the window so callers can decide how long
 * a shared link is good for before re-listing targets.
 */
const LIVE_VIEW_URL_TTL_MS = 5 * 60 * 1000;

function isMissingBrowserSession(error: unknown): boolean {
  // Browser Run uses 404 for unknown ids and 410 after keep_alive expiry.
  return (
    error instanceof BrowserRenderingError &&
    (error.status === 404 || error.status === 410)
  );
}

function formatToolValidationError(
  connector: string,
  tool: string,
  errors: OutputUnit[]
): string {
  // A failing property emits both a generic parent `properties` error and a
  // specific child error. Prefer the latter so the model sees what to fix.
  const specific = errors.filter((error) => error.keyword !== "properties");
  const relevant = specific.length > 0 ? specific : errors;
  const details = relevant.map((error) => {
    const location = error.instanceLocation
      .replace(/^#\/?/, "")
      .replaceAll("~1", "/")
      .replaceAll("~0", "~")
      .replaceAll("/", ".");
    return `${location ? ` at ${location}` : ""}: ${error.error}`;
  });
  return `Invalid arguments for ${connector}.${tool}${details.join(";")}`;
}

/**
 * Rewrite the hosted Live View UI's `mode` query param (`tab` | `devtools`).
 * The raw `devtoolsFrontendUrl` is returned unchanged when no mode is asked
 * for or the URL can't be parsed.
 */
function applyLiveViewMode(rawUrl: string, mode?: LiveViewMode): string {
  if (!mode) return rawUrl;
  try {
    const url = new URL(rawUrl);
    url.searchParams.set("mode", mode === "devtools" ? "devtools" : "tab");
    return url.toString();
  } catch {
    return rawUrl;
  }
}

interface CachedSocket {
  session: CdpSession;
  /** Browser Run session id the socket is attached to (undefined for cdpUrl). */
  browserSessionId?: string;
  /**
   * Live CDP session id per attach handle, valid for this socket only.
   * Rebuilt lazily after a reconnect (sockets are per-pass).
   */
  attached: Map<string, string>;
}

/**
 * `cdp.attachToTarget` returns `{ sessionId: "target:<targetId>" }` — a stable
 * handle instead of the raw CDP session id. (The object shape mirrors the real
 * `Target.attachToTarget` response, which is what models reach for.) Raw ids
 * are connection-scoped: a run that pauses
 * for approval resumes on a fresh WebSocket where the old id is invalid, and
 * the durable replay log would otherwise pin the stale value. The handle is a
 * pure function of the target, so replayed code computes identical arguments,
 * and `send` resolves it to a live session id on the current socket —
 * re-attaching lazily after a reconnect.
 */
const ATTACH_HANDLE_PREFIX = "target:";

/**
 * Codemode connector exposing a live browser over the Chrome DevTools
 * Protocol as the `cdp` global.
 *
 * Per-execution resources are keyed by the codemode `executionId`:
 *
 * - The Browser Run session id is stored durably under `cdp:exec:<id>`, so a
 *   run that pauses for approval reconnects to the *same* browser when it
 *   resumes — even on a fresh instance.
 * - The CDP WebSocket is per-pass: `onPassEnd` disconnects it (a paused run
 *   holds no socket), and the next pass reconnects from the stored id.
 * - `disposeExecution` deletes the session unless it was promoted to the
 *   shared slot via `cdp.startSession()` (dynamic mode).
 *
 * Locks on the session store are held only around store reads/writes, never
 * across network calls to Browser Run or while a socket is open.
 */
export class BrowserConnector extends CodemodeConnector {
  #options: BrowserConnectorOptions;
  #sockets = new Map<string, CachedSocket>();
  #connecting = new Map<string, Promise<CdpSession>>();

  constructor(
    ctx: DurableObjectState | ExecutionContext,
    options: BrowserConnectorOptions
  ) {
    super(ctx, {});
    if (!options.cdpUrl && !options.browser) {
      throw new Error(
        "BrowserConnector requires either 'browser' (Fetcher binding) or 'cdpUrl'"
      );
    }
    if (options.browser && !options.store) {
      throw new Error(
        "BrowserConnector requires 'store' when using the Browser Rendering binding"
      );
    }
    if (options.session?.browser === "kitesurf") {
      if (
        options.session.mode !== undefined &&
        options.session.mode !== "one-shot"
      ) {
        throw new Error('Kitesurf only supports session mode "one-shot"');
      }
      if (options.session.keepAliveMs) {
        throw new Error("Kitesurf does not support keepAliveMs");
      }
      if (options.session.recording) {
        throw new Error("Kitesurf does not support session recording");
      }
    }
    this.#options = options;
  }

  name(): string {
    return "cdp";
  }

  protected instructions(): string {
    const mode = this.#mode();
    const lines = [
      "Issue CDP calls sequentially — never in parallel (no Promise.all): call order is recorded for durable replay.",
      "cdp.send returns the CDP method result directly, not the outer JSON-RPC envelope: Target.createTarget returns { targetId }, Runtime.evaluate returns { result: { value } }, and Page.captureScreenshot returns { data }.",
      "Browser-/Target-scoped commands (Target.createTarget, Target.getTargets) need no sessionId. Page-scoped commands (Page.navigate, Runtime.evaluate) require one: `const { sessionId } = await cdp.attachToTarget({ targetId });` then pass it to every page-scoped send. Use this helper instead of sending Target.attachToTarget manually.",
      "Write large textual outputs such as page dumps to a file or workspace and pass around small references. For screenshots, follow the host tool's documented screenshot result format.",
      "If a command fails or times out, check cdp.getDebugLog() for recent protocol traffic."
    ];
    if (this.#isKitesurf()) {
      lines.push(
        "Kitesurf implements a subset of CDP and does not expose protocol discovery.",
        "Kitesurf is scoped to this connection. Complete the whole browser task in one execution and do not pause for approval: the browser cannot reconnect when execution resumes.",
        "Kitesurf Page.navigate may resolve before layout and paint. Before a screenshot: call Page.enable; navigate; poll Runtime.evaluate with returnByValue: true until result.value reports document.readyState === 'complete' and non-empty body text; then wait 1000ms before Page.captureScreenshot. Do not capture if readiness fails."
      );
    } else {
      lines.push(
        "Use cdp.spec() to discover commands, events, and types when unsure.",
        "When a step needs a human (login, MFA, CAPTCHA, sensitive input), call cdp.getLiveViewUrl() to get a link they can open to control the browser live — surface it, then make an approval-gated call so the run pauses until they're done."
      );
    }
    if (mode === "one-shot") {
      lines.push(
        "The browser session lasts for this execution only and is closed when it ends."
      );
    } else if (mode === "reuse") {
      lines.push(
        "The browser session is shared and persists across executions — tabs and state you leave behind will still be there next time."
      );
    } else {
      lines.push(
        "The browser session is one-shot by default. If browser state must persist after this execution (e.g. a logged-in page), call cdp.startSession() to keep it alive for later executions."
      );
    }
    return lines.join("\n");
  }

  protected override tool(name: string, tool: ConnectorTool): ConnectorTool {
    if (!tool.inputSchema) return tool;

    // Both types describe draft-7 schemas, but @cfworker's `Schema` type is
    // narrower than JSONSchema7 around boolean subschemas.
    const schema = tool.inputSchema as unknown as Schema;
    const validator = new Validator(schema, "7", false);
    return {
      ...tool,
      execute: async (args, ctx) => {
        // Browser's argumentless tools are documented as `cdp.spec()` etc.;
        // treat an omitted argument as the empty object their schemas expect.
        const input = args === undefined ? {} : args;
        const result = validator.validate(input);
        if (!result.valid) {
          throw new Error(
            formatToolValidationError(this.name(), name, result.errors)
          );
        }
        return await tool.execute(input, ctx);
      }
    };
  }

  protected tools(): ConnectorTools {
    const tools: ConnectorTools = {
      send: {
        description:
          "Send a CDP command and return its method result directly (the outer JSON-RPC envelope is removed). Page-scoped commands require a sessionId — pass the handle returned by attachToTarget.",
        inputSchema: {
          type: "object",
          properties: {
            method: {
              type: "string",
              description: 'CDP method, e.g. "Target.createTarget"'
            },
            params: {
              type: "object",
              description: "CDP command parameters"
            },
            sessionId: {
              type: "string",
              description:
                "Session handle from attachToTarget, for page-scoped commands"
            },
            timeoutMs: {
              type: "number",
              description: "Per-command timeout override in milliseconds"
            }
          },
          required: ["method"]
        },
        execute: async (args, ctx) => {
          const { method, params, sessionId, timeoutMs } = args as {
            method: string;
            params?: unknown;
            sessionId?: string;
            timeoutMs?: number;
          };
          const executionId = this.#executionId(ctx);
          const socket = await this.#socket(executionId);
          const resolved = await this.#resolveSessionHandle(
            executionId,
            sessionId
          );
          try {
            return await socket.send(method, params, {
              sessionId: resolved,
              timeoutMs
            });
          } catch (err) {
            // "Method wasn't found" is almost always one of two model
            // mistakes. Teach the fix instead of leaving a bare protocol
            // error: either the method is actually an event (events can't be
            // sent), or a page-scoped command went to the browser-level
            // session because no sessionId was passed.
            if (
              err instanceof Error &&
              /-32601|wasn't found/.test(err.message)
            ) {
              if (await this.#isSpecEvent(method)) {
                throw new Error(
                  `${err.message}. '${method}' is a CDP *event*, not a ` +
                    `command — it cannot be called via cdp.send. To wait for ` +
                    `page state, poll instead (e.g. Runtime.evaluate of ` +
                    `document.readyState until "complete").`
                );
              }
              if (!sessionId) {
                throw new Error(
                  `${err.message}. Page-scoped commands need a sessionId: ` +
                    `const { sessionId } = await cdp.attachToTarget({ targetId }); ` +
                    `then pass sessionId to cdp.send.`
                );
              }
            }
            throw err;
          }
        }
      },

      attachToTarget: {
        description: this.#isKitesurf()
          ? "Attach to a target (tab) and return { sessionId } to pass to page-scoped send calls for this connection."
          : "Attach to a target (tab) and return { sessionId } — a stable session handle to pass as sessionId in page-scoped send calls. The handle stays valid across pauses/resumes.",
        inputSchema: {
          type: "object",
          properties: {
            targetId: {
              type: "string",
              description: "Target id from Target.createTarget/getTargets"
            },
            timeoutMs: { type: "number" }
          },
          required: ["targetId"]
        },
        outputSchema: {
          type: "object",
          properties: {
            sessionId: {
              type: "string",
              description: this.#isKitesurf()
                ? "Session handle for page-scoped send calls on this connection"
                : "Stable session handle for page-scoped send calls (valid across pauses/resumes)"
            }
          },
          required: ["sessionId"]
        },
        execute: async (args, ctx) => {
          const { targetId, timeoutMs } = args as {
            targetId: string;
            timeoutMs?: number;
          };
          const executionId = this.#executionId(ctx);
          await this.#attach(executionId, targetId, timeoutMs);
          return { sessionId: `${ATTACH_HANDLE_PREFIX}${targetId}` };
        }
      },

      spec: {
        description:
          "Return the searchable Chrome DevTools Protocol spec: domains with their commands, events, and types. Use it to discover method names and capabilities.",
        replay: "reexecute",
        inputSchema: { type: "object", properties: {} },
        execute: async (): Promise<SearchableCdpSpec> => {
          if (this.#isKitesurf()) {
            throw new Error(
              "Kitesurf does not expose a CDP protocol specification"
            );
          }
          return loadCdpSpec(this.#options);
        }
      },

      getDebugLog: {
        description:
          "Return recent CDP protocol traffic (sends, receives, warnings) for this execution's connection — useful to diagnose failures and timeouts.",
        replay: "reexecute",
        inputSchema: {
          type: "object",
          properties: {
            limit: {
              type: "number",
              description: "Max entries to return (default 50)"
            }
          }
        },
        execute: async (args, ctx) => {
          const { limit } = (args ?? {}) as { limit?: number };
          const socket = await this.#socket(this.#executionId(ctx));
          return socket.getDebugLog(limit);
        }
      },

      clearDebugLog: {
        description: "Clear the CDP debug log for this execution's connection.",
        inputSchema: { type: "object", properties: {} },
        execute: async (_args, ctx) => {
          const socket = await this.#socket(this.#executionId(ctx));
          socket.clearDebugLog();
          return null;
        }
      }
    };

    if (this.#isKitesurf()) {
      delete tools.spec;
    } else {
      tools.getLiveViewUrl = {
        description:
          "Return a Live View URL for a tab — a link a human can open in a " +
          "browser to watch and control this session in real time. Use it " +
          "for human-in-the-loop steps (login, MFA, CAPTCHA, sensitive " +
          "input): get the URL, surface it to the user, then make a call " +
          "that pauses for approval so they can act before the run " +
          "continues. The URL is valid for ~5 minutes.",
        replay: "reexecute",
        inputSchema: {
          type: "object",
          properties: {
            targetId: {
              type: "string",
              description:
                "Target (tab) id to view; defaults to the first open page"
            },
            mode: {
              type: "string",
              enum: ["tab", "devtools"],
              description:
                "tab = standalone interactive page view (best for handoff); " +
                "devtools = full DevTools inspector panel"
            }
          }
        },
        execute: async (args, ctx) => {
          const { targetId, mode } = (args ?? {}) as {
            targetId?: string;
            mode?: LiveViewMode;
          };
          return this.#liveViewUrl(this.#executionId(ctx), { targetId, mode });
        }
      };
    }

    const mode = this.#mode();
    if (mode === "reuse" || mode === "dynamic") {
      tools.startSession = {
        description:
          mode === "dynamic"
            ? "Promote the current browser session into the shared slot so it persists after this execution. Later executions reuse it. Returns the session info."
            : "Ensure the shared browser session exists and return its info.",
        inputSchema: { type: "object", properties: {} },
        execute: async (_args, ctx) =>
          this.#startSession(this.#executionId(ctx))
      };
      tools.sessionInfo = {
        description:
          "Return info about the shared browser session (id and open targets), or null when none exists.",
        replay: "reexecute",
        inputSchema: { type: "object", properties: {} },
        execute: async () => (await this.sessionInfo()) ?? null
      };
      tools.closeSession = {
        description:
          "Close the shared browser session, discarding its tabs and state.",
        inputSchema: { type: "object", properties: {} },
        execute: async (_args, ctx) => {
          await this.#closeReusableFor(this.#executionId(ctx));
          return null;
        }
      };
      tools.resetSession = {
        description:
          "Close the shared browser session and start a fresh one. Returns the new session info.",
        inputSchema: { type: "object", properties: {} },
        execute: async (_args, ctx) =>
          this.#resetSession(this.#executionId(ctx))
      };
    }

    return tools;
  }

  // ---------------------------------------------------------------------
  // Lifecycle hooks
  // ---------------------------------------------------------------------

  /**
   * A pass is over (completed, errored, or paused awaiting approval) — drop
   * the CDP socket. The Browser Run session itself stays alive; a resume
   * reconnects from the durably stored session id.
   */
  override async onPassEnd(
    executionId: string,
    status: PassEndStatus
  ): Promise<void> {
    this.#dropSocket(executionId);
    if (!this.#isKitesurf() || status !== "paused") return;

    const key = this.#execKey(executionId);
    const lock = await this.#store.acquireLock(key);
    try {
      const stored = await this.#store.get(key);
      // Only a Kitesurf marker is gone with its connection; a session left by
      // an earlier Chromium run is still live and must stay reclaimable.
      if (stored?.sessionId.startsWith(KITESURF_SESSION_PREFIX)) {
        await this.#store.set(key, { ...stored, closedAt: Date.now() });
      }
    } finally {
      await lock.release();
    }
  }

  /**
   * The execution is terminal — delete its Browser Run session unless it was
   * promoted to the shared slot via `cdp.startSession()`.
   */
  override async disposeExecution(
    executionId: string,
    _status: ExecutionEndStatus
  ): Promise<void> {
    this.#dropSocket(executionId);
    if (!this.#options.browser) return;

    if (this.#isKitesurf()) {
      const key = this.#execKey(executionId);
      const lock = await this.#store.acquireLock(key);
      // An entry written before the connector was switched to Kitesurf still
      // holds a real Browser Run session that has to be closed.
      let stale: StoredBrowserSession | undefined;
      try {
        const stored = await this.#store.get(key);
        if (
          stored &&
          stored.closedAt === undefined &&
          !stored.sessionId.startsWith(KITESURF_SESSION_PREFIX)
        ) {
          stale = stored;
        }
        await this.#store.delete(key);
      } finally {
        await lock.release();
      }
      if (stale) {
        try {
          await deleteBrowserSession(this.#options.browser, stale.sessionId);
        } catch (error) {
          console.warn(
            `[agents/browser] Failed to delete Browser Run session ${stale.sessionId} for execution ${executionId}`,
            error
          );
        }
      }
      return;
    }

    const store = this.#options.store;
    const execKey = this.#execKey(executionId);
    const lock = await store.acquireLock(execKey);
    // Decide and update storage under the lock; the Browser Rendering delete
    // happens after release (locks wrap storage only).
    let toClose: StoredBrowserSession | undefined;
    try {
      const stored = await store.get(execKey);
      if (!stored) return;

      let promoted = false;
      if (this.#mode() === "dynamic") {
        const shared = await store.get(this.#reuseKey());
        promoted = shared?.sessionId === stored.sessionId;
      }

      if (!promoted && stored.closedAt === undefined) {
        toClose = stored;
      }
      await store.delete(execKey);
    } finally {
      await lock.release();
    }

    if (toClose) {
      try {
        await deleteBrowserSession(this.#options.browser, toClose.sessionId);
      } catch (error) {
        console.warn(
          `[agents/browser] Failed to delete Browser Run session ${toClose.sessionId} for execution ${executionId}`,
          error
        );
      }
    }
  }

  // ---------------------------------------------------------------------
  // Host-side helpers — for callables and scheduled tasks on the agent.
  // ---------------------------------------------------------------------

  /** Info about the shared (reuse/promoted) session, if one exists. */
  async sessionInfo(): Promise<BrowserSessionInfo | undefined> {
    if (!this.#options.browser || this.#isKitesurf()) return undefined;
    const store = this.#options.store;
    const key = this.#reuseKey();
    const lock = await store.acquireLock(key);
    let stored: StoredBrowserSession | undefined;
    try {
      stored = await store.get(key);
    } finally {
      await lock.release();
    }
    if (!stored) return undefined;
    try {
      return {
        sessionId: stored.sessionId,
        targets: await listBrowserTargets(
          this.#options.browser,
          stored.sessionId
        )
      };
    } catch (error) {
      if (isMissingBrowserSession(error)) {
        await this.#deleteStoredEntry(key, stored.sessionId);
        return undefined;
      }
      throw error;
    }
  }

  /**
   * Live View URLs for the shared (reuse/promoted) session's tabs, or
   * `undefined` when no shared session exists. Each URL opens an interactive,
   * real-time view of the browser — surface them in your UI (or send them via
   * Slack/email) to let a human watch or take over a running session
   * (human-in-the-loop). URLs are valid for ~5 minutes; call again for fresh
   * ones. Only meaningful with the Browser Rendering binding in
   * `reuse`/`dynamic` mode.
   */
  async liveView(options?: {
    mode?: LiveViewMode;
  }): Promise<BrowserLiveView | undefined> {
    const info = await this.sessionInfo();
    if (!info) return undefined;
    const targets = (info.targets ?? [])
      .filter((target) => target.devtoolsFrontendUrl)
      .map((target) => ({
        targetId: target.id,
        url: applyLiveViewMode(target.devtoolsFrontendUrl!, options?.mode),
        pageUrl: target.url,
        title: target.title,
        type: target.type
      }));
    return {
      sessionId: info.sessionId,
      targets,
      expiresInMs: LIVE_VIEW_URL_TTL_MS
    };
  }

  /** Close the shared (reuse/promoted) session, if one exists. */
  async closeSession(): Promise<void> {
    if (!this.#options.browser || this.#isKitesurf()) return;
    await this.#closeStoredSession(this.#reuseKey());
  }

  /**
   * Close stored sessions (shared and per-execution) idle past the threshold.
   * Per-execution entries normally die with `disposeExecution` (or the
   * codemode runtime's `expirePaused`); the sweep is the backstop for crashed
   * hosts. Call it from a recurring alarm/scheduled task.
   *
   * Active executions bump their entry's `updatedAt` on use, so only runs
   * idle past `maxExecIdleMs` (default 24h) are reclaimed. A swept
   * per-execution entry is kept as a tombstone (`closedAt`) rather than
   * deleted, so a later resume fails with a clear "session expired" error
   * instead of silently continuing in a fresh browser; tombstones are
   * deleted once they age past the threshold again.
   *
   * Kitesurf entries are reclaimed the same way, minus the Browser Run
   * delete: their stored id is a synthetic per-execution marker, not a
   * session, and the connection died with the execution.
   */
  async sweep(
    options?: BrowserConnectorSweepOptions
  ): Promise<BrowserConnectorSweepResult> {
    if (!this.#options.browser) return { swept: [] };
    const browser = this.#options.browser;
    const store = this.#options.store;
    const maxIdleMs =
      options?.maxIdleMs ??
      this.#options.session?.keepAliveMs ??
      DEFAULT_SWEEP_IDLE_MS;
    const maxExecIdleMs = options?.maxExecIdleMs ?? DEFAULT_EXEC_SWEEP_IDLE_MS;

    const keys = new Set<string>([this.#reuseKey()]);
    if (store.list) {
      for (const key of (await store.list("cdp:")).keys()) {
        keys.add(key);
      }
    }

    const swept: Array<{ key: string; sessionId: string }> = [];
    for (const key of keys) {
      const isExec = key.startsWith(EXEC_KEY_PREFIX);
      const idleMs = isExec ? maxExecIdleMs : maxIdleMs;
      const lock = await store.acquireLock(key);
      let toClose: StoredBrowserSession | undefined;
      try {
        const stored = await store.get(key);
        if (!stored) continue;
        const now = Date.now();
        if (stored.closedAt !== undefined) {
          // Tombstone from a previous sweep — its session is already gone.
          // Drop it once it has aged past the threshold again.
          if (now - stored.closedAt >= idleMs) await store.delete(key);
          continue;
        }
        if (now - stored.updatedAt < idleMs) continue;
        if (isExec) {
          await store.set(key, { ...stored, closedAt: now });
        } else {
          await store.delete(key);
        }
        toClose = stored;
      } finally {
        await lock.release();
      }
      // Kitesurf entries hold a synthetic marker rather than a Browser Run
      // session id: the connection died with its execution, so there is
      // nothing remote to close. Entries written before the connector was
      // reconfigured still need their session deleted, so this is decided per
      // entry rather than from the current options.
      if (!toClose.sessionId.startsWith(KITESURF_SESSION_PREFIX)) {
        try {
          await deleteBrowserSession(browser, toClose.sessionId);
        } catch (error) {
          console.warn(
            `[agents/browser] Sweep failed to delete Browser Run session ${toClose.sessionId}`,
            error
          );
        }
      }
      swept.push({ key, sessionId: toClose.sessionId });
    }
    return { swept };
  }

  // ---------------------------------------------------------------------
  // Session + socket resolution
  // ---------------------------------------------------------------------

  #isKitesurf(): boolean {
    return this.#options.session?.browser === "kitesurf";
  }

  #mode(): "one-shot" | "reuse" | "dynamic" {
    return this.#options.session?.mode ?? "one-shot";
  }

  #execKey(executionId: string): string {
    return `${EXEC_KEY_PREFIX}${executionId}`;
  }

  #reuseKey(): string {
    return `${REUSE_KEY_PREFIX}${this.#options.session?.key ?? "default"}`;
  }

  #executionId(ctx?: ToolExecuteContext): string {
    if (!ctx?.executionId) {
      throw new Error(
        "BrowserConnector requires an execution context — use it through createCodemodeRuntime"
      );
    }
    return ctx.executionId;
  }

  #dropSocket(executionId: string): void {
    const cached = this.#sockets.get(executionId);
    if (!cached) return;
    this.#sockets.delete(executionId);
    cached.session.disconnect();
  }

  /** Attach the current socket to a target, caching the live CDP session id. */
  async #attach(
    executionId: string,
    targetId: string,
    timeoutMs?: number
  ): Promise<string> {
    const socket = await this.#socket(executionId);
    const cached = this.#sockets.get(executionId);
    const handle = `${ATTACH_HANDLE_PREFIX}${targetId}`;
    const existing = cached?.attached.get(handle);
    if (existing) return existing;
    const live = await socket.attachToTarget(targetId, { timeoutMs });
    cached?.attached.set(handle, live);
    return live;
  }

  /**
   * List the current tabs (targets) for this execution's browser, including
   * their `devtoolsFrontendUrl` (the Live View link). For the binding this is
   * the current Browser Run session; for an externally-managed `cdpUrl` it is
   * the standard CDP `/json/list`.
   */
  async #liveTargets(executionId: string): Promise<BrowserTargetInfo[]> {
    if (this.#options.cdpUrl) {
      const endpoint = new URL("/json/list", this.#options.cdpUrl).toString();
      const response = await fetch(endpoint, {
        headers: this.#options.cdpHeaders
      });
      if (!response.ok) {
        throw new Error(
          `Failed to list targets at ${endpoint}: ${response.status}`
        );
      }
      const payload = (await response.json()) as unknown;
      return Array.isArray(payload) ? (payload as BrowserTargetInfo[]) : [];
    }

    const browser = this.#options.browser;
    if (!browser) throw new Error("BrowserConnector has no browser binding");
    if (this.#isKitesurf()) {
      throw new Error("Kitesurf does not support Browser Run Live View");
    }
    // Ensure a session + socket exist, then read the resolved Browser Run id.
    await this.#socket(executionId);
    const sessionId = this.#sockets.get(executionId)?.browserSessionId;
    if (!sessionId) return [];
    return listBrowserTargets(browser, sessionId);
  }

  /** Build a Live View URL for one tab in this execution's browser. */
  async #liveViewUrl(
    executionId: string,
    options: { targetId?: string; mode?: LiveViewMode }
  ): Promise<BrowserLiveViewUrl> {
    const targets = await this.#liveTargets(executionId);
    const target = options.targetId
      ? targets.find((candidate) => candidate.id === options.targetId)
      : (targets.find(
          (candidate) =>
            candidate.type === "page" && candidate.devtoolsFrontendUrl
        ) ?? targets.find((candidate) => candidate.devtoolsFrontendUrl));

    if (!target) {
      throw new Error(
        options.targetId
          ? `No target ${options.targetId} found. List tabs with Target.getTargets or open one with Target.createTarget.`
          : "No browser tab is open yet. Create one (Target.createTarget) before requesting a Live View URL."
      );
    }
    if (!target.devtoolsFrontendUrl) {
      throw new Error(
        `Target ${target.id} has no Live View URL (devtoolsFrontendUrl). Live View needs the Browser Rendering binding.`
      );
    }

    return {
      url: applyLiveViewMode(target.devtoolsFrontendUrl, options.mode),
      targetId: target.id,
      expiresInMs: LIVE_VIEW_URL_TTL_MS
    };
  }

  /**
   * Resolve a model-facing session handle to the live CDP session id on the
   * current socket, re-attaching lazily after a reconnect. Raw CDP session
   * ids (from manual Target.attachToTarget sends) pass through untouched.
   */
  /**
   * True when `method` is a CDP *event* (e.g. `Page.loadEventFired`) rather
   * than a command. Used only on the `send` failure path to produce a better
   * error; any spec-loading failure just means no extra hint.
   */
  async #isSpecEvent(method: string): Promise<boolean> {
    try {
      if (this.#isKitesurf()) return false;
      const spec = await loadCdpSpec(this.#options);
      const domain = method.split(".")[0];
      return spec.domains.some(
        (d) => d.name === domain && d.events.some((e) => e.event === method)
      );
    } catch {
      return false;
    }
  }

  async #resolveSessionHandle(
    executionId: string,
    sessionId?: string
  ): Promise<string | undefined> {
    if (!sessionId?.startsWith(ATTACH_HANDLE_PREFIX)) return sessionId;
    const targetId = sessionId.slice(ATTACH_HANDLE_PREFIX.length);
    return this.#attach(executionId, targetId);
  }

  /**
   * Get or open the CDP socket for an execution. Concurrent calls for the
   * same execution (model code that ignores the "sequential calls" rule and
   * uses Promise.all) share one in-flight connect instead of racing and
   * leaking the loser's WebSocket.
   */
  #socket(executionId: string): Promise<CdpSession> {
    const inFlight = this.#connecting.get(executionId);
    if (inFlight) return inFlight;
    const promise = this.#socketInner(executionId).finally(() => {
      if (this.#connecting.get(executionId) === promise) {
        this.#connecting.delete(executionId);
      }
    });
    this.#connecting.set(executionId, promise);
    return promise;
  }

  async #socketInner(executionId: string): Promise<CdpSession> {
    if (this.#options.cdpUrl) {
      const cached = this.#sockets.get(executionId);
      if (cached) return cached.session;
      const session = await connectUrl(this.#options.cdpUrl, {
        timeoutMs: this.#options.timeout,
        headers: this.#options.cdpHeaders
      });
      this.#sockets.set(executionId, { session, attached: new Map() });
      return session;
    }

    const browser = this.#options.browser;
    if (!browser) throw new Error("BrowserConnector has no browser binding");

    if (this.#isKitesurf()) {
      const cached = this.#sockets.get(executionId);
      if (cached) return cached.session;

      const key = this.#execKey(executionId);
      const lock = await this.#store.acquireLock(key);
      try {
        if (await this.#store.get(key)) {
          throw new Error(
            "Kitesurf's connection was closed and cannot be resumed; start a new execution"
          );
        }
        const now = Date.now();
        await this.#store.set(key, {
          sessionId: `${KITESURF_SESSION_PREFIX}${crypto.randomUUID()}`,
          createdAt: now,
          updatedAt: now
        });
      } finally {
        await lock.release();
      }

      try {
        const session = await connectBrowser(browser, {
          browser: "kitesurf",
          timeoutMs: this.#options.timeout
        });
        this.#sockets.set(executionId, { session, attached: new Map() });
        return session;
      } catch (error) {
        const failed = await this.#readStored(key);
        if (failed) await this.#deleteStoredEntry(key, failed.sessionId);
        throw error;
      }
    }

    const stored = await this.#resolveSession(executionId);
    const cached = this.#sockets.get(executionId);
    if (cached?.browserSessionId === stored.sessionId) {
      return cached.session;
    }
    if (cached) this.#dropSocket(executionId);

    const session = await connectBrowserSession(
      browser,
      stored.sessionId,
      this.#options.timeout
    );
    this.#sockets.set(executionId, {
      session,
      browserSessionId: stored.sessionId,
      attached: new Map()
    });
    return session;
  }

  /**
   * Resolve the Browser Run session for an execution:
   *
   * - An existing `cdp:exec:<id>` entry wins. If its session is gone (e.g.
   *   expired while the run was paused), the run fails with a clear error
   *   rather than silently continuing in a fresh browser.
   * - In `reuse` mode the shared session is used (created if missing).
   * - In `dynamic` mode an alive shared session is used; otherwise a fresh
   *   per-execution session is created.
   * - In `one-shot` mode a fresh per-execution session is created.
   */
  async #resolveSession(executionId: string): Promise<StoredBrowserSession> {
    const browser = this.#options.browser;
    if (!browser) throw new Error("BrowserConnector has no browser binding");
    const mode = this.#mode();
    const execKey = this.#execKey(executionId);

    if (mode === "reuse") {
      return this.#ensureStoredSession(this.#reuseKey());
    }

    // one-shot / dynamic: a session this execution already opened wins.
    const existing = await this.#readStored(execKey);
    if (existing) {
      if (existing.closedAt === undefined && (await this.#isAlive(existing))) {
        // Keep the entry fresh so the sweep backstop never reclaims an
        // active (or recently resumed) execution's session.
        if (Date.now() - existing.updatedAt >= EXEC_TOUCH_INTERVAL_MS) {
          await this.#touchStored(execKey, existing);
        }
        return existing;
      }
      await this.#deleteStoredEntry(execKey, existing.sessionId);
      throw new Error(
        `Browser session ${existing.sessionId} expired or was swept while this execution was paused — the run cannot continue. Start a new execution.`
      );
    }

    if (mode === "dynamic") {
      const shared = await this.#readStored(this.#reuseKey());
      if (shared) {
        if (await this.#isAlive(shared)) {
          await this.#touchStored(this.#reuseKey(), shared);
          return shared;
        }
        await this.#deleteStoredEntry(this.#reuseKey(), shared.sessionId);
      }
    }

    // Create a fresh per-execution session. The Browser Rendering call
    // happens outside the store lock; the lock only guards the commit, so
    // two concurrent tool calls for the same execution don't double-create
    // (the loser's session is deleted).
    return this.#createAndCommit(execKey);
  }

  /**
   * Get the stored session under `key`, validating and creating as needed.
   *
   * Network calls (the liveness probe, session creation) happen OUTSIDE the
   * store lock — locks wrap storage only, so a hung Browser Rendering call
   * can't serialize every other operation on this key. The lock is
   * re-acquired to commit, with a sessionId re-check to detect a concurrent
   * swap; on a swap the new entry is re-validated from the top.
   */
  async #ensureStoredSession(key: string): Promise<StoredBrowserSession> {
    const browser = this.#options.browser;
    if (!browser) throw new Error("BrowserConnector has no browser binding");
    const store = this.#store;

    for (let attempt = 0; attempt < 3; attempt++) {
      const existing = await store.get(key);
      if (!existing) return this.#createAndCommit(key);

      const alive = await this.#isAlive(existing);
      const lock = await store.acquireLock(key);
      try {
        const current = await store.get(key);
        if (current?.sessionId !== existing.sessionId) {
          // Entry was swapped while we probed — validate the new one.
          continue;
        }
        if (alive) {
          const refreshed = { ...current, updatedAt: Date.now() };
          await store.set(key, refreshed);
          return refreshed;
        }
        await store.delete(key);
      } finally {
        await lock.release();
      }
      // Dead entry deleted — the next iteration creates a fresh session.
    }
    throw new Error(
      `Browser session entry ${key} kept changing concurrently — retry`
    );
  }

  /**
   * Create a Browser Run session (outside any lock) and commit it under
   * `key`. If a concurrent caller committed first, their entry wins and the
   * redundant session is deleted best-effort.
   */
  async #createAndCommit(key: string): Promise<StoredBrowserSession> {
    const browser = this.#options.browser;
    if (!browser) throw new Error("BrowserConnector has no browser binding");
    const store = this.#store;

    const info = await createBrowserSession(browser, {
      keepAliveMs: this.#options.session?.keepAliveMs,
      recording: this.#options.session?.recording
    });
    const now = Date.now();
    const stored: StoredBrowserSession = {
      sessionId: info.sessionId,
      createdAt: now,
      updatedAt: now
    };

    const lock = await store.acquireLock(key);
    let winner: StoredBrowserSession | undefined;
    try {
      const raced = await store.get(key);
      if (raced) {
        winner = raced;
      } else {
        await store.set(key, stored);
      }
    } finally {
      await lock.release();
    }

    if (winner) {
      try {
        await deleteBrowserSession(browser, stored.sessionId);
      } catch (error) {
        console.warn(
          `[agents/browser] Failed to delete redundant Browser Run session ${stored.sessionId}`,
          error
        );
      }
      return winner;
    }
    return stored;
  }

  async #isAlive(stored: StoredBrowserSession): Promise<boolean> {
    const browser = this.#options.browser;
    if (!browser) return false;
    try {
      await listBrowserTargets(browser, stored.sessionId);
      return true;
    } catch (error) {
      if (isMissingBrowserSession(error)) return false;
      throw error;
    }
  }

  // ---------------------------------------------------------------------
  // Session tools (reuse/dynamic)
  // ---------------------------------------------------------------------

  async #startSession(executionId: string): Promise<BrowserSessionInfo> {
    const browser = this.#options.browser;
    if (!browser) {
      throw new Error("startSession requires the Browser Rendering binding");
    }
    const store = this.#options.store;
    const reuseKey = this.#reuseKey();

    if (this.#mode() === "dynamic") {
      // Promote this execution's session into the shared slot, if it has one.
      const exec = await this.#readStored(this.#execKey(executionId));
      if (exec && exec.closedAt === undefined) {
        const lock = await store.acquireLock(reuseKey);
        let replaced: StoredBrowserSession | undefined;
        try {
          const shared = await store.get(reuseKey);
          if (shared?.sessionId !== exec.sessionId) {
            replaced = shared;
            await store.set(reuseKey, { ...exec, updatedAt: Date.now() });
          }
        } finally {
          await lock.release();
        }
        if (replaced) {
          try {
            await deleteBrowserSession(browser, replaced.sessionId);
          } catch (error) {
            console.warn(
              `[agents/browser] Failed to delete replaced Browser Run session ${replaced.sessionId}`,
              error
            );
          }
        }
        return {
          sessionId: exec.sessionId,
          targets: await listBrowserTargets(browser, exec.sessionId)
        };
      }
    }

    const stored = await this.#ensureStoredSession(reuseKey);
    return {
      sessionId: stored.sessionId,
      targets: await listBrowserTargets(browser, stored.sessionId)
    };
  }

  async #resetSession(executionId: string): Promise<BrowserSessionInfo> {
    const browser = this.#options.browser;
    if (!browser) {
      throw new Error("resetSession requires the Browser Rendering binding");
    }
    await this.#closeReusableFor(executionId);
    const stored = await this.#ensureStoredSession(this.#reuseKey());
    return {
      sessionId: stored.sessionId,
      targets: await listBrowserTargets(browser, stored.sessionId)
    };
  }

  /**
   * Close the shared session from inside an execution. If this execution's
   * socket is attached to that session, drop it first.
   */
  async #closeReusableFor(executionId: string): Promise<void> {
    const reuseKey = this.#reuseKey();
    const stored = await this.#readStored(reuseKey);
    if (!stored) return;
    const cached = this.#sockets.get(executionId);
    if (cached?.browserSessionId === stored.sessionId) {
      this.#dropSocket(executionId);
    }
    await this.#closeStoredSession(reuseKey);
    // In reuse mode the execution continues against a fresh shared session on
    // the next send. In dynamic mode the exec entry (if any) still points at
    // the closed session; clear it so the next send fails loudly instead of
    // silently targeting a deleted browser.
    const exec = await this.#readStored(this.#execKey(executionId));
    if (exec?.sessionId === stored.sessionId) {
      await this.#deleteStoredEntry(this.#execKey(executionId), exec.sessionId);
    }
  }

  // ---------------------------------------------------------------------
  // Store access — locks held only around the store operation itself.
  // ---------------------------------------------------------------------

  get #store(): BrowserSessionStore {
    const store = this.#options.store;
    if (!store) {
      throw new Error(
        "BrowserConnector session storage requires the Browser Rendering binding"
      );
    }
    return store;
  }

  async #readStored(key: string): Promise<StoredBrowserSession | undefined> {
    const store = this.#store;
    const lock = await store.acquireLock(key);
    try {
      return await store.get(key);
    } finally {
      await lock.release();
    }
  }

  async #writeStored(key: string, value: StoredBrowserSession): Promise<void> {
    const store = this.#store;
    const lock = await store.acquireLock(key);
    try {
      await store.set(key, value);
    } finally {
      await lock.release();
    }
  }

  async #touchStored(key: string, value: StoredBrowserSession): Promise<void> {
    await this.#writeStored(key, { ...value, updatedAt: Date.now() });
  }

  /** Delete the store entry only if it still points at `sessionId`. */
  async #deleteStoredEntry(key: string, sessionId: string): Promise<void> {
    const store = this.#store;
    const lock = await store.acquireLock(key);
    try {
      const current = await store.get(key);
      if (current?.sessionId === sessionId) {
        await store.delete(key);
      }
    } finally {
      await lock.release();
    }
  }

  /** Delete the stored entry under `key` and its Browser Run session. */
  async #closeStoredSession(key: string): Promise<void> {
    const browser = this.#options.browser;
    if (!browser) return;
    const store = this.#options.store;
    const lock = await store.acquireLock(key);
    let stored: StoredBrowserSession | undefined;
    try {
      stored = await store.get(key);
      if (stored) await store.delete(key);
    } finally {
      await lock.release();
    }
    if (stored) {
      await deleteBrowserSession(browser, stored.sessionId);
    }
  }
}
